import { ensureSeconds, countdownText, parseLocal } from '@/utils/datetime';
import { buildAnalogies } from '@/utils/analogies';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

function remainingSeconds(startIso: string, endIso: string | undefined, now: Date) {
  const start = parseLocal(ensureSeconds(startIso));
  const end = endIso ? parseLocal(ensureSeconds(endIso)) : undefined;

  if (now < start) return Math.max(0, Math.floor((start.getTime() - now.getTime()) / 1000));
  if (end && now >= start && now < end) return Math.max(0, Math.floor((end.getTime() - now.getTime()) / 1000));
  const ref = end ?? start; // time since (for past)
  return Math.max(0, Math.floor((now.getTime() - ref.getTime()) / 1000));
}

/** Seeded RNG helpers (stable randomness per event) */
function xmur3(str: string) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
}
function mulberry32(a: number) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function makeRng(seedStr: string) {
  const seed = xmur3(seedStr)();
  return mulberry32(seed);
}

/** 10–30 seconds between analogy switches (seeded) */
function nextPhaseMs(rng: () => number) {
  const sec = 10 + Math.floor(rng() * 21); // 10..30
  return sec * 1000;
}

export function CountdownHero({
  startIso,
  endIso,
  now,
  randSeed, // pass a per-event stable key (e.g., event id)
}: {
  startIso: string;
  endIso?: string;
  now: Date;
  randSeed?: string;
}) {
  // Live timer text (updates every second via parent-provided `now`)
  const ct = useMemo(() => countdownText(startIso, endIso, now), [startIso, endIso, now]);

  /** -------- Auto-fit text (no layout shift): scale text to pill width -------- */
  const wrapperRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const span = textRef.current;
    if (!wrapper || !span) return;

    const compute = () => {
      span.style.fontSize = '';
      const cs = getComputedStyle(wrapper);
      const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const available = Math.max(0, wrapper.clientWidth - padX);
      const needed = span.scrollWidth;
      if (!available || !needed) { setScale(1); return; }
      const next = Math.max(0.6, Math.min(1, available / needed));
      setScale(next);
    };

    compute();
    const ro = new ResizeObserver(() => compute());
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [ct.text]);

  /** -------- Always-visible analogy line (cycles randomly per event) -------- */
  const rng = useMemo(
    () => makeRng((randSeed ?? '') + '|' + startIso + '|' + (endIso ?? '')),
    [randSeed, startIso, endIso]
  );

  const secs = useMemo(() => remainingSeconds(startIso, endIso, now), [startIso, endIso, now]);
  const analogies = useMemo(() => buildAnalogies(secs), [secs]);

  // Initial index is seeded random so different events start on different hints
  const [hintIdx, setHintIdx] = useState<number>(() => {
    if (!analogies.length) return 0;
    return Math.floor(rng() * analogies.length);
  });
  // Key only changes when we switch analogy (drives subtle fade)
  const [hintKey, setHintKey] = useState<number>(0);
  const [phaseUntil, setPhaseUntil] = useState<number>(() => Date.now() + nextPhaseMs(rng));

  // Switch analogy only when the phase elapses (driven by `now` tick)
  useEffect(() => {
    const t = Date.now();
    if (t < phaseUntil || analogies.length === 0) return;

    // pick a new random index != current
    let next = analogies.length > 1 ? Math.floor(rng() * analogies.length) : 0;
    if (next === hintIdx && analogies.length > 1) {
      next = (next + 1) % analogies.length; // ensure change
    }
    setHintIdx(next);
    setHintKey(k => k + 1);
    setPhaseUntil(t + nextPhaseMs(rng));
  }, [now, phaseUntil, analogies.length, rng, hintIdx]);

  // If the set of analogies shrinks/expands, keep the index in range
  useEffect(() => {
    if (analogies.length === 0) { setHintIdx(0); return; }
    if (hintIdx >= analogies.length) setHintIdx(analogies.length - 1);
  }, [analogies.length, hintIdx]);

  const hintText = analogies[hintIdx] ?? '';

  return (
    <div className="countdown-stack">
      <div className="countdown-hero" data-status={ct.status} aria-live="off" ref={wrapperRef}>
        <span
          ref={textRef}
          className="herotext"
          style={{ fontSize: `calc(var(--countdown-hero-size) * ${scale})` }}
        >
          {ct.text}
        </span>
      </div>

      {/* Always visible hint line; subtle fade ONLY when it changes */}
      <div className="countdown-hint" aria-live="polite" aria-atomic="true">
        <span key={hintKey} className="hinttext hint-anim" title={hintText}>
          {hintText}
        </span>
      </div>
    </div>
  );
}
